import React, {useState, useEffect, useMemo} from 'react';
import {
    Card,
    Tag,
    Table,
    Tabs,
    Typography,
    Modal,
    Form,
    Button,
    message,
    Upload,
    Divider,
    Select,
    Space, Tooltip
} from 'antd';
import {
    UserAddOutlined,
    EyeOutlined,
    EditOutlined,
} from '@ant-design/icons';
import dayjs from "dayjs";
import {dashboardColumns} from './constants';
import apiClient from './apiHelper';
import {ENDPOINTS, REQUEST_TYPES, MESSAGES} from './apiConfig';
import UserForm from './userForm';
import UserDetailView from "./UserDetailView";
import {DashboardFilterBar} from './FilterBar';

const {Title} = Typography;

const Dashboard = ({user}) => {
    const [activeTab, setActiveTab] = useState('all');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [allFilters, setAllFilters] = useState({users: [], companies: [], projects: [], managers: []});
    const [backfillFilters, setBackfillFilters] = useState({users: [], companies: [], projects: [], managers: []});
    const [managerList, setManagerList] = useState([]);
    const [externalManagerList, setExternalManagerList] = useState([]);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [addForm] = Form.useForm();
    const [updateForm] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const showModal = () => setIsModalOpen(true);

    const userId = user?.email;
    const userRole = user?.role;

    const fetchUsers = async () => {
        setLoading(true);

        try {
            const res = await apiClient(ENDPOINTS.USER.FETCH_USERS, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_USERS
                })
            });

            const userList = res?.items || [];
            setUsers(userList);

        } catch (error) {
            message.error(MESSAGES.ERROR.FETCH_USERS);
        } finally {
            setLoading(false);
        }
    };

    const getManagerList = async () => {
        try {
            const res = await apiClient(ENDPOINTS.MANAGER.FETCH_MANAGER, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_MANAGER
                })
            });

            const rawList = res.managers || res || [];

            const sortedList = rawList
                .sort((a, b) => {
                    const nameA = a.email || "";
                    const nameB = b.email || "";
                    return nameA.localeCompare(nameB);
                });

            const FilteredList = rawList
                .filter(m => m.email !== user?.email)
                .sort((a, b) => {
                    const nameA = a.email || "";
                    const nameB = b.email || "";
                    return nameA.localeCompare(nameB);
                });

            if (userRole === "manager") {
                setManagerList(FilteredList);
                return FilteredList;
            } else {
                setManagerList(sortedList);
                return sortedList;
            }

        } catch (err) {
            message.error(MESSAGES.ERROR.FETCH_MANAGER);
        }
    };

    const getExternalManagerList = async () => {
        try {
            const res = await apiClient(ENDPOINTS.MANAGER.FETCH_MANAGER, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_EXTERNAL_MANAGER
                })
            });
            const rawList = res.externalManagers || res || [];

            const sortedFilteredList = rawList
                .filter(m => m.email !== user?.email)
                .sort((a, b) => {
                    const nameA = a.email || "";
                    const nameB = b.email || "";
                    return nameA.localeCompare(nameB);
                });

            setExternalManagerList(sortedFilteredList);
            return sortedFilteredList;

        } catch (err) {
            message.error(MESSAGES.ERROR.FETCH_EXTERNAL_MANAGER);
        }
    };

    const backfillBaseList = useMemo(() => {
        return users.filter(u => u.can_backfill === true || u.can_backfill === "Yes");
    }, [users]);

    const getUniqueOptions = (data, key, isArrayField = false) => {
        const uniqueValues = new Set();
        data.forEach((item) => {
            const value = item[key];
            if (isArrayField && Array.isArray(value)) {
                value.forEach(v => v && uniqueValues.add(v));
            } else if (typeof value === 'string' && value.includes(',') && key === 'project') {
                value.split(',').forEach(v => uniqueValues.add(v.trim()));
            } else if (value) {
                uniqueValues.add(value);
            }
        });
        return Array.from(uniqueValues).sort().map(val => ({label: val, value: val}));
    };

    const allOptions = useMemo(() => ({
        user: getUniqueOptions(users, 'email'),
        company: getUniqueOptions(users, 'company_name'),
        project: getUniqueOptions(users, 'project', true),
        manager: getUniqueOptions(users, 'manager_ids', true)
    }), [users]);

    const backfillOptions = useMemo(() => ({
        user: getUniqueOptions(backfillBaseList, 'email'),
        company: getUniqueOptions(backfillBaseList, 'company_name'),
        project: getUniqueOptions(backfillBaseList, 'project', true),
        manager: getUniqueOptions(backfillBaseList, 'manager_ids', true)
    }), [backfillBaseList]);

    const filteredAllUsers = useMemo(() => {
        return users.filter((u) => {
            const matchUser = allFilters.users.length ? allFilters.users.includes(u.email) : true;
            const matchCompany = allFilters.companies.length ? allFilters.companies.includes(u.company_name) : true;
            const userProjects = Array.isArray(u.project) ? u.project : [];
            const matchProject = allFilters.projects.length ? allFilters.projects.some(p => userProjects.includes(p)) : true;
            const allManagers = [...(u.manager_ids || []), ...(u.external_manager_ids || [])];
            const matchManager = allFilters.managers.length ? allFilters.managers.some(m => allManagers.includes(m)) : true;
            return matchUser && matchCompany && matchProject && matchManager;
        });
    }, [users, allFilters]);

    const filteredBackfillUsers = useMemo(() => {
        return backfillBaseList.filter((u) => {
            const matchUser = backfillFilters.users.length ? backfillFilters.users.includes(u.email) : true;
            const matchCompany = backfillFilters.companies.length ? backfillFilters.companies.includes(u.company_name) : true;

            const userProjects = Array.isArray(u.project) ? u.project : [];
            const matchProject = backfillFilters.projects.length ? backfillFilters.projects.some(p => userProjects.includes(p)) : true;

            const allManagers = [...(u.manager_ids || []), ...(u.external_manager_ids || [])];
            const matchManager = backfillFilters.managers.length ? backfillFilters.managers.some(m => allManagers.includes(m)) : true;

            return matchUser && matchCompany && matchProject && matchManager;
        });
    }, [backfillBaseList, backfillFilters]);

    const columns = [
        ...dashboardColumns,
        {
            title: 'Project',
            dataIndex: 'project',
            key: 'project',
            width: 130,
            render: (projects) => {
                const projectArray = Array.isArray(projects)
                    ? projects
                    : (projects ? projects.split(',').map(p => p.trim()) : []);

                if (projectArray.length === 0) return <span style={{color: '#bfbfbf'}}>N/A</span>;

                return (
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                        {projectArray.map((p) => (
                            <Tooltip title={p} key={p} mouseEnterDelay={0.5}>
                                <Tag
                                    color="gray"
                                    key={p}
                                    style={{
                                        margin: 0,
                                        fontSize: '11px',
                                        maxWidth: '100px',
                                        display: 'inline-block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'bottom'
                                    }}
                                >
                                    {p}
                                </Tag>
                            </Tooltip>
                        ))}
                    </div>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            width: 110,
            onCell: (record) => ({
                className: record.isDisabled ? "disabled-action" : "",
            }),
            render: (_, record) => (
                <Space className="space-x-2">
                    <Button
                        type="link"
                        className="black-btn"
                        icon={<EyeOutlined/>}
                        onClick={() => handleView(record)}
                        size="small"
                    >
                        View
                    </Button>
                    {["manager", "admin"].includes(userRole) ?
                        <Button
                            type="primary"
                            className="black-btn"
                            icon={<EditOutlined/>}
                            onClick={() => openUpdateModal(record)}
                            size="small"
                        >
                            Update
                        </Button>
                        : []
                    }

                </Space>
            ),
        }
    ];

    const generatedPassword = (length = 10) => {
        const charset = {
            upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
            lower: "abcdefghijklmnopqrstuvwxyz",
            number: "0123456789",
            symbol: "!@#$%^&*?"
        };

        let password = [
            charset.upper[crypto.getRandomValues(new Uint32Array(1))[0] % charset.upper.length],
            charset.lower[crypto.getRandomValues(new Uint32Array(1))[0] % charset.lower.length],
            charset.number[crypto.getRandomValues(new Uint32Array(1))[0] % charset.number.length],
            charset.symbol[crypto.getRandomValues(new Uint32Array(1))[0] % charset.symbol.length],
        ];

        const allChars = Object.values(charset).join("");
        const extraLength = length - password.length;

        const randomValues = new Uint32Array(extraLength);
        crypto.getRandomValues(randomValues);

        for (let i = 0; i < extraLength; i++) {
            password.push(allChars[randomValues[i] % allChars.length]);
        }

        return password
            .map(value => ({value, sort: crypto.getRandomValues(new Uint32Array(1))[0]}))
            .sort((a, b) => a.sort - b.sort)
            .map(({value}) => value)
            .join("");
    };

    const uploadFile = async (file, objectType) => {
        try {
            const response = await apiClient(ENDPOINTS.UPLOADS.FILE, {
                method: 'POST',
                body: JSON.stringify({
                    filename: file.name,
                    object_type: objectType,
                    request_type: REQUEST_TYPES.UPLOAD_FILE
                })
            });
            const {presigned_url, s3_key} = response;

            const upload = await fetch(presigned_url, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (upload.ok) {
                return s3_key;
            }
        } catch (error) {
            message.error("Upload failed");
        }
    };

    const handleViewDocument = async (s3Path) => {
        if (!s3Path) {
            message.warning("No document path available.");
            return;
        }

        try {
            const response = await apiClient(ENDPOINTS.UPLOADS.FILE, {
                method: 'POST',
                body: JSON.stringify({
                    s3_key: s3Path,
                    request_type: REQUEST_TYPES.DOWNLOAD_FILE
                })
            });

            if (response && response.download_url) {
                window.open(response.download_url, '_blank', 'noopener,noreferrer');
            }
        } catch (error) {
            message.error("Failed to load document.");
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await addForm.validateFields();
            setSubmitLoading(true);

            const isManagerRole = ['manager', 'external manager'].includes(values.role);
            const isInternalEmployee = values.email?.endsWith("amns.in");

            let ndaS3Path = "";
            let otherS3Paths = [];

            if (values.NDA_doc && values.NDA_doc.length > 0) {
                const file = values.NDA_doc[0].originFileObj;
                const s3Key = await uploadFile(file, 'NDA');
                ndaS3Path = s3Key || `Timesheet/NDA/${file.name}`;
            }

            if (values.other_doc && values.other_doc.length > 0) {
                const uploadPromises = values.other_doc.map(async (fileItem) => {
                    const file = fileItem.originFileObj;
                    const s3Key = await uploadFile(file, 'Others');
                    return s3Key || `Timesheet/Others/${file.name}`;
                });
                otherS3Paths = await Promise.all(uploadPromises);
            }

            const password = generatedPassword();
            const [startDate, endDate] = values.date_range || [];

            const managerIdsList = Array.from(new Set([
                userId,
                ...(values.selected_managers || [])
            ]));

            let payload = {
                request_type: REQUEST_TYPES.REGISTER,
                role: values.role || "user",
                password: password,
                created_by: userId,
            };

            if (isManagerRole) {
                payload = {
                    ...payload,
                    email: values.email,
                    full_name: "",
                    company_name: "N/A",
                    project: [],
                    manager_ids: "",
                    mobile_number: "",
                    work_start_date: "",
                    work_end_date: "",
                    NDA_doc: "",
                    other_doc: ""
                };
            } else {
                payload = {
                    ...payload,
                    email: isInternalEmployee ? values.email : (values.email ? values.email.toLowerCase().trim() : ""),
                    full_name: values.full_name,
                    mobile_number: values.mobile_number,
                    company_name: values.company_name,
                    project: Array.isArray(values.project) ? values.project : (values.project ? [values.project] : []),
                    work_start_date: startDate ? startDate.format("YYYY-MM-DD") : "",
                    work_end_date: endDate ? endDate.format("YYYY-MM-DD") : "",
                    vpn_profile: values.vpn_access === "Required" ? values.vpn_profile : "",
                    domain_profile: values.domain_access === "Required" ? values.domain_profile : "",
                    assigned_company_email: values.company_email === "Required" ? values.assigned_company_email : "",
                    manager_ids: managerIdsList,
                    external_manager_ids: values.selected_external_managers,
                    NDA_doc: ndaS3Path,
                    other_doc: otherS3Paths.join(', ')
                };
            }

            await apiClient(ENDPOINTS.AUTH.REGISTER, {
                method: "POST",
                body: JSON.stringify(payload),
            });

            getManagerList();
            getExternalManagerList();
            message.success(`User registered! Credentials sent to ${values.email}`);
            setIsModalOpen(false);
            addForm.resetFields();

            if (typeof fetchUsers === "function") fetchUsers();

        } catch (error) {
            if (error.errorFields) {
                message.error("Please fill in all required fields.");
            } else {
                message.error(error?.message);
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    const normFile = (e) => {
        if (Array.isArray(e)) return e;
        return e && e.fileList;
    };

    const MAX_FILE_MB = 10;

    const isAllowedType = (file) => {
        const pdf = "application/pdf";
        const xls =
            "application/vnd.ms-excel";
        const xlsx =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

        return [pdf, xls, xlsx].includes(file.type);
    };

    const beforeUploadPdfExcel = (file) => {
        if (!isAllowedType(file)) {
            message.error("Only PDF (.pdf) and Excel (.xls, .xlsx) files are allowed.");
            return Upload.LIST_IGNORE;
        }

        const isSizeOk = file.size / 1024 / 1024 <= MAX_FILE_MB;
        if (!isSizeOk) {
            message.error(`File must be smaller than ${MAX_FILE_MB} MB.`);
            return Upload.LIST_IGNORE;
        }

        return false;
    };

    const handleView = (record) => {
        setSelectedRow(record);
        setViewModalOpen(true);
    };

    const transformToFileList = (s3Path) => {
        if (!s3Path) return [];
        return s3Path.split(', ').filter(Boolean).map((path, index) => ({
            uid: `rc-upload-${Date.now()}-${index}`,
            name: path.split('/').pop(),
            status: 'done',
            url: path,
        }));
    };

    const openUpdateModal = (record) => {
        setSelectedRow(record);

        let initialManagers = [];

        if (Array.isArray(record.manager_ids)) {
            initialManagers = record.manager_ids;
        } else if (record.manager_id) {
            initialManagers = [record.manager_id];
        }
        const filteredManagers = initialManagers.filter(email => email !== user?.email);


        updateForm.setFieldsValue({
            ...record,
            date_range: [
                record.work_start_date ? dayjs(record.work_start_date) : undefined,
                record.work_end_date ? dayjs(record.work_end_date) : undefined,
            ],
            vpn_access: record.vpn_access ?? "Not Required",
            domain_access: record.domain_access ?? "Not Required",
            company_email: record.company_email ?? "Not Required",
            NDA_doc: transformToFileList(record.NDA_doc),
            other_doc: transformToFileList(record.other_doc),
            can_backfill: record.can_backfill,
            selected_managers: userRole === "manager" ? filteredManagers : initialManagers,
            selected_external_managers: record.external_manager_ids,
        });

        setIsUpdateModalOpen(true);
    };

    const handleUpdateSubmit = async () => {
        try {
            await updateForm.validateFields();
            setUpdateLoading(true);

            const values = updateForm.getFieldsValue();
            const [start, end] = values.date_range || [];

            const managerIdsList = values.selected_managers || [];
            if (userRole === "manager") {
                if (user?.email && !managerIdsList.includes(user.email)) {
                    managerIdsList.push(user.email);
                }
            }

            let finalNdaPath = "";
            if (values.NDA_doc && values.NDA_doc.length > 0) {
                const fileObj = values.NDA_doc[0];
                if (fileObj.originFileObj) {
                    finalNdaPath = await uploadFile(fileObj.originFileObj, 'NDA');
                } else {
                    finalNdaPath = fileObj.url;
                }
            }

            const otherDocsArray = await Promise.all(
                (values.other_doc || []).map(async (fileObj) => {
                    if (fileObj.originFileObj) {
                        return await uploadFile(fileObj.originFileObj, 'Others');
                    }
                    return fileObj.url;
                })
            );

            const payload = {
                ...values,
                request_type: REQUEST_TYPES.UPDATE_USER,
                user_id: selectedRow?.user_id,
                project: Array.isArray(values.project) ? values.project : (values.project ? [values.project] : []),
                work_start_date: start ? start.format("YYYY-MM-DD") : "",
                work_end_date: end ? end.format("YYYY-MM-DD") : "",
                vpn_profile: values.vpn_access === "Required" ? values.vpn_profile : "",
                domain_profile: values.domain_access === "Required" ? values.domain_profile : "",
                assigned_company_email: values.company_email === "Required" ? values.assigned_company_email : "",
                manager_ids: managerIdsList,
                external_manager_ids: values.selected_external_managers,
                NDA_doc: finalNdaPath,
                other_doc: otherDocsArray.filter(Boolean).join(', '),
                can_backfill: values.can_backfill,
            };

            await apiClient(ENDPOINTS.USER.UPDATE_USER, {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            message.success(MESSAGES.SUCCESS.UPDATE_USER);
            setIsUpdateModalOpen(false);
            fetchUsers();

        } catch (error) {
            if (error.errorFields) {
                message.error("Please fill in all required fields.");
            } else {
                message.error(error?.message);
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        getManagerList();
        getExternalManagerList();
    }, []);

    const renderFilterBar = (currentFilters, setFilters, tabOptions) => (
        <DashboardFilterBar
            options={tabOptions}
            filters={currentFilters}
            onFilterChange={setFilters}
            onRefresh={fetchUsers}
            loading={loading}
            showManagerFilter={userRole === 'admin'}
        />
    );

    const tabItems = [
        {
            key: 'all',
            label: `All Users (${filteredAllUsers.length})`,
            children: (
                <>
                    {renderFilterBar(allFilters, setAllFilters, allOptions)}
                    <Table
                        columns={columns}
                        dataSource={filteredAllUsers}
                        rowKey="user_id"
                        loading={loading}
                        pagination={filteredAllUsers.length > 10}
                        size="middle"
                    />
                </>
            ),
        },
        ...(userRole === "admin" ? [{
            key: 'backfill',
            label: `Backfill Enabled (${filteredBackfillUsers.length})`,
            children: (
                <>
                    {renderFilterBar(backfillFilters, setBackfillFilters, backfillOptions)}
                    <Table
                        columns={columns}
                        dataSource={filteredBackfillUsers}
                        rowKey="user_id"
                        loading={loading}
                        pagination={filteredBackfillUsers.length > 10}
                        size="middle"
                    />
                </>
            ),
        }] : [])
    ];

    return (
        <div>
            {userRole === "admin" ?
                <Card title={<Title level={4} style={{marginBottom: 0}}>Resource Management</Title>}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabItems}
                    />
                </Card> : <Card title={<Title level={4} style={{marginBottom: 0}}>User list</Title>}>
                    {renderFilterBar(allFilters, setAllFilters, allOptions)}
                    <Table
                        columns={columns}
                        dataSource={filteredAllUsers}
                        rowKey="user_id"
                        loading={loading}
                        pagination={filteredAllUsers.length > 10}
                        size="middle"
                    />
                </Card>
            }
            {['admin', 'manager'].includes(userRole) ?
                <div className="absolute bottom-6 right-6 cursor-pointer" onClick={showModal}>
                    <UserAddOutlined
                        className="text-4xl text-red-600 bg-white hover:bg-gray-200 p-3 rounded-full shadow-md"/>
                </div> : []
            }

            <Modal
                title={<Title level={4} style={{marginBottom: 0}}>Add User</Title>}
                open={isModalOpen}
                onCancel={() => {
                    addForm.resetFields();
                    setIsModalOpen(false);
                }}
                footer={null}
                centered
                width={900}
                closeIcon={<span className="font-bold">✕</span>}
            >
                <Divider style={{marginTop: 0}}/>
                <div className="max-h-[80vh] overflow-y-auto overflow-x-hidden pr-2">
                    <UserForm
                        form={addForm}
                        onSubmit={handleSubmit}
                        loading={submitLoading}
                        managerList={managerList}
                        externalManagerList={externalManagerList}
                        normFile={normFile}
                        beforeUploadPdfExcel={beforeUploadPdfExcel}
                        userRole={userRole}
                    />
                </div>
            </Modal>

            <Modal
                title={<Title level={4} style={{fontWeight: "bold"}}>View User Details</Title>}
                open={viewModalOpen}
                onCancel={() => setViewModalOpen(false)}
                footer={false}
                centered
                width={800}
            >
                <Divider style={{marginTop: 0}}/>
                {selectedRow ? (
                    <UserDetailView
                        data={selectedRow}
                        handleViewDocument={handleViewDocument}
                    />
                ) : (
                    <div>No user selected</div>
                )}
            </Modal>

            <Modal
                title={<Title level={4} style={{fontWeight: "bold"}}>Update User Details</Title>}
                open={isUpdateModalOpen}
                onCancel={() => {
                    updateForm.resetFields();
                    setIsUpdateModalOpen(false);
                }}
                footer={null}
                centered
                width={900}
                closeIcon={<span className="font-bold">✕</span>}
            >
                <Divider style={{marginTop: 0}}/>

                <div className="max-h-[80vh] overflow-y-auto overflow-x-hidden pr-2">
                    <UserForm
                        isUpdate={true}
                        form={updateForm}
                        onSubmit={handleUpdateSubmit}
                        loading={updateLoading}
                        managerList={managerList}
                        externalManagerList={externalManagerList}
                        userRole={userRole}
                        normFile={normFile}
                        beforeUploadPdfExcel={beforeUploadPdfExcel}
                        onPreview={(file) => handleViewDocument(file.url || file.response?.s3_key)}
                    />

                </div>
            </Modal>

        </div>
    );
};

export default Dashboard;